<template>
  <div class="alert alert-danger" role="alert" v-if="orgInfo && !orgInfo.connectedToStripe">
    Please set up your Stripe account so that participants can register and pay for your
    {{ terms?.ProgramPlural.toLowerCase() }}.
    {{ terms?.ProgramPlural }} with a cost attached will not show on the website until your Stripe account has been set
    up.
    Go to {{ setting?.platformShortName }} > Settings > Stripe
  </div>
  <main>
    <div class="row form-input" v-if="isLoadingVenues || isLoadingCategories || isLoadingLogin || isLoadingProgram">
      <div class="col-sm-12 text-center">
        <div class="spinner-grow spinner-grow-sm" role="status"></div>
      </div>
    </div>
    <div class="container theme-platformbrand px-4" v-else>
      <div class="row">
        <div class="col col-xs-12 col-sm-10 col-md-8 p-0" v-if="isValidProgram">
          <ul class="nav nav-tabs nav-pills" id="myTab" role="tablist" v-if="activeCategory">
            <li class="nav-item mr-1" v-for="category in categories" :key="category.id">
              <button :class="`nav-link clubhouse-dropdown-item ${activeCategory.id == category.id ? 'active branding-bg' : ''
                } ${category.id == 1 ? 'mygolf' : 'getintogolf'}`" :id="`#${category.id}-tab`" data-bs-toggle="pill"
                type="button" @click="() => setActiveTab(category)" role="tab"
                :aria-controls="`${category.description}`" aria-selected="true"
                :disabled="programUID != null && activeCategory.id != category.id">
                {{ category.description }} <i v-if="activeCategory.id == category.id" class="bi bi-check-circle"></i>
              </button>
            </li>
          </ul>
          <div class="tab-content" v-if="activeCategory">
            <div v-for="category in categories" role="tabpanel" :class="`tab-pane fade show ${activeCategory.id == category.id ? 'active' : ''
              }`" :id="`${category.id}`" :aria-labelledby="`${category.id}-tab`" :key="category.id">
              <div class="row p-2">
                <div class="col-sm-12 mt-3 p-1">
                  <h5>
                    {{
                      programUID
                        ? `Edit ${activeCategory.description}`
                        : `Create a ${activeCategory.description}`
                    }}
                    {{ terms?.Program }}
                  </h5>
                </div>
                <div class="col mt-2">
                  <ProgramForm :currency.sync="currency" :programData.sync="programData" :isDuplicate.sync="isDuplicate"
                    :programCategoryId.sync="category.id" :activeCategory.sync="activeCategory" :venues.sync="venues" :orgInfo.sync="orgInfo" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col col-xs-12 col-sm-10 col-md-8 p-0" v-else>
          <h3>You do not have permissions to access this.</h3>
        </div>
      </div>
    </div>
  </main>
</template>

<script setup lang="ts">
import { useProgramCategoriesStore } from "@/stores/program-categories-store";
import { useAuthStore } from "@/stores/auth-store";
import { useVenueStore } from "@/stores/venue-store";
import type { IProgramCategoryVm } from "@/types/IProgramCategoryVm";
import { storeToRefs } from "pinia";
import { onMounted, ref, computed } from "vue";
import ProgramForm from "../components/ProgramForm.vue";
import { useRoute } from "vue-router";
import { useProgramStore } from "@/stores/program-store";
import { useSettingStore } from "@/stores/setting-store";
import { useTermsStore } from "@/stores/language-terms";
const { terms } = storeToRefs(useTermsStore());
const { setting } = storeToRefs(useSettingStore());
const { getSetting } = useSettingStore();

const route = useRoute();

const { getLoginOrgInfo } = useAuthStore();
const { loadProgramCategories } = useProgramCategoriesStore();
const { categories, isLoadingCategories } = storeToRefs(useProgramCategoriesStore());
const { getVenuesByCurrentLogin } = useVenueStore();
const { getProgramById } = useProgramStore();
const { venues, isLoadingVenues } = storeToRefs(useVenueStore());
const { orgInfo, isLoadingLogin } = storeToRefs(useAuthStore());
const activeCategory = ref();
const { isLoadingProgram, programData } = storeToRefs(useProgramStore());
const { getDefaultCurrency } = useCurrencyStore();
import { useCurrencyStore } from "@/stores/currency-store";
const { currency } = storeToRefs(useCurrencyStore());
const isValidProgram = ref(true);

onMounted(async () => {
  await getLoginOrgInfo();
  await loadProgramCategories();
  await getVenuesByCurrentLogin();
  await getDefaultCurrency();
  await getSetting();

  if (programUID.value) {
    const id = programUID.value as string;
    await getProgramById(id, orgInfo?.value?.clubId);

    if (programData.value) {
      isValidProgram.value = true;
      const target = categories.value.find(
        (x) => x.id == programData.value.programCategoryId
      );
      if (target) {
        activeCategory.value = target;
      }
    } else {
      isValidProgram.value = false;
    }
  } else {
    programData.value = null;
    if (categories.value && categories.value.length > 0) {
      activeCategory.value = categories.value[0];
    }
  }
});

const setActiveTab = (category: IProgramCategoryVm) => {
  activeCategory.value = category;
};

const programUID = computed(() => {
  return route.params.programUID;
});

const isDuplicate = computed(() => {
  return route.params.d && route.params.d == "1";
});
</script>

<style scoped>
label.form-label {
  font-size: 13px;
}

.nav-tabs {
  border-bottom: none;
}

.nav-tabs .nav-link {
  border: solid 1px #ccc;
}

.nav-tabs .nav-item {
  min-width: 300px;
  text-align: center;
}

.nav-link.clubhouse-dropdown-item.active {
  border-bottom: none;
  padding-bottom: 4px !important;
}

.nav-link.clubhouse-dropdown-item {
  width: 100%;
}

.nav-item {
  flex: 1;
}

.nav-item .nav-link {
  color: black;
  caret-color: transparent;
  text-transform: none;
  font-size: 13px;
}

.nav-link.active.mygolf {
  background-color: #035ced;
}

.nav-link.mygolf {
  border: 1px solid #035ced;
}

.nav-link.active.getintogolf {
  background-color: #00C68A;
  border: 1px solid #00C68A;
}

.nav-link.getintogolf {
  border: 1px solid #00C68A;
}
</style>
